import React from 'react';
import styled from 'styled-components';
import { Flex, Text, FlexColumn } from 'core/components';
import { formatNumber } from 'app/util/utils';

const scaleMax = 0.999;

const UsageIndicator = styled.div`
  background: ${(props) => (props.theme.name === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)')};
  border-radius: 10px;
  border: 2px solid transparent;
  display: flex;
  height: 12px;
  margin: 2px 0px 8px;
  overflow: hidden;
  width: 100%;
`;

const UsageDot = styled.div`
  background: ${({ theme, bg }) => theme.colors[bg]};
  border-radius: 18px;
  background-image: linear-gradient(
    -45deg,
    hsla(0, 0%, 100%, 0.2) 25%,
    transparent 0,
    transparent 50%,
    hsla(0, 0%, 100%, 0.2) 0,
    hsla(0, 0%, 100%, 0.2) 75%,
    transparent 0
  );
  border: 1px solid #fff;
  height: 18px;
  margin-right: 4px;
  width: 18px;
  min-width: 18px;
`;

const UsageSegment = styled.div`
  background: ${({ isPending, isOverCredits, theme }) => {
    if (isOverCredits) {
      return theme.colors.danger;
    }

    return isPending ? theme.colors.primary : theme.colors.muted;
  }};

  background-image: linear-gradient(
    -45deg,
    hsla(0, 0%, 100%, 0.2) 25%,
    transparent 0,
    transparent 50%,
    hsla(0, 0%, 100%, 0.2) 0,
    hsla(0, 0%, 100%, 0.2) 75%,
    transparent 0
  );
  width: ${(props) => `${props.percent}%`};
  border-radius: ${({ isPending }) => (isPending ? '0 10px 10px 0' : '10px 0 0 10px')};
  border-left: ${({ isPending, usedCredits }) => (isPending && usedCredits ? '2px solid #fff' : 'none')};
  height: 8px;
`;

function CreditUsageIndicator({ totalAvailableCredits, usedCredits, pendingTestUsage, showUsedCredits = true }) {
  const percentUtilized = (usedCredits / totalAvailableCredits) * 100;
  const pendingTestPercent = (pendingTestUsage / totalAvailableCredits) * 100;
  const isOverCredits = usedCredits + pendingTestUsage >= totalAvailableCredits;
  const isOverLimit = percentUtilized > 100;
  const projectedTotalCredits = usedCredits + pendingTestUsage;
  const projectedRemainingCredits = totalAvailableCredits - projectedTotalCredits;

  const formattedActual = formatNumber(usedCredits, {
    fix: usedCredits > 1000 ? 1 : 0,
    scaleMax
  });
  const formattedEom = formatNumber(projectedTotalCredits, {
    fix: projectedTotalCredits > 1000 ? 1 : 0,
    scaleMax
  });
  const formattedEomRemaining = formatNumber(projectedRemainingCredits, {
    fix: projectedRemainingCredits > 1000 ? 1 : 0,
    useAbsVal: false,
    scaleMax
  });
  const usedColor = usedCredits < totalAvailableCredits ? 'muted' : 'danger';
  const projectedColor = projectedTotalCredits < totalAvailableCredits ? 'primary' : 'danger';

  return (
    <>
      <UsageIndicator>
        <UsageSegment
          percent={isOverLimit ? 100 : percentUtilized}
          usedCredits={usedCredits}
          isOverCredits={isOverLimit}
        />
        {!isOverLimit && (
          <UsageSegment
            percent={pendingTestPercent}
            isPending
            usedCredits={usedCredits}
            isOverCredits={isOverCredits}
          />
        )}
      </UsageIndicator>
      <Flex justifyContent="space-between" alignItems="center">
        <Flex justifyContent="flex-start">
          {showUsedCredits && (
            <Flex mr={1} alignItems="center">
              <UsageDot bg={usedColor} />
              <FlexColumn ml="4px">
                <Text small muted>
                  Used
                </Text>
                <Text as="div" mr="4px" fontWeight="bold" small>
                  {formattedActual.displayFull}
                </Text>
              </FlexColumn>
            </Flex>
          )}
          <Flex mr={1} alignItems="center">
            <UsageDot bg={projectedColor} />
            <FlexColumn ml="4px">
              <Text small muted>
                Projected Total
              </Text>
              <Text as="div" mr="4px" fontWeight="bold" small>
                {formattedEom.displayFull}
              </Text>
            </FlexColumn>
          </Flex>
        </Flex>

        <Flex alignItems="center">
          <FlexColumn ml={1}>
            <Text small muted>
              Projected Remaining
            </Text>
            <Text mr="4px" fontWeight="bold" small>
              {formattedEomRemaining.displayFull}
            </Text>
          </FlexColumn>
        </Flex>
      </Flex>
    </>
  );
}

export default CreditUsageIndicator;
